import { isEnergyCardInvoiceId } from '../hookServices/energyCard';

export function isUahEnergyCard(card) {
  return card?.unit === 'UAH';
}

/**
 * Sum of energy-card balances from /api/energy-card/active.
 * Backend remainingKwh is free + hold (full card balance for the client).
 * Expired (archived) cards are excluded from the live balance.
 */
export function getEnergyCardBalanceKwh(activeEnergyCards) {
  if (!Array.isArray(activeEnergyCards) || activeEnergyCards.length === 0) {
    return 0;
  }
  return activeEnergyCards.reduce((sum, card) => {
    if (card?.expired || isUahEnergyCard(card)) {
      return sum;
    }
    return sum + (Number(card?.remainingKwh) || 0);
  }, 0);
}

/** Sum of UAH energy-card balances (грн). Expired cards excluded. */
export function getEnergyCardBalanceUah(activeEnergyCards) {
  if (!Array.isArray(activeEnergyCards) || activeEnergyCards.length === 0) {
    return 0;
  }
  return activeEnergyCards.reduce((sum, card) => {
    if (card?.expired || !isUahEnergyCard(card)) {
      return sum;
    }
    return sum + (Number(card?.remainingUah) || 0);
  }, 0);
}

export function hasKwhEnergyCards(activeEnergyCards) {
  if (!Array.isArray(activeEnergyCards)) return false;
  return activeEnergyCards.some(card => !card?.expired && !isUahEnergyCard(card));
}

export function hasUahEnergyCards(activeEnergyCards) {
  if (!Array.isArray(activeEnergyCards)) return false;
  return activeEnergyCards.some(card => !card?.expired && isUahEnergyCard(card));
}

function isActiveChargingState(state) {
  return state === 'IN_PROGRESS' || state === 'WAITING';
}

function getHistoryEnergyCardChargedKwh(historyData) {
  if (!Array.isArray(historyData)) {
    return 0;
  }
  return historyData.reduce((sum, item) => {
    if (!isActiveChargingState(item?.state) || !isEnergyCardInvoiceId(item?.invoiceId)) {
      return sum;
    }
    return sum + Number(item.energyWth || 0) / 1000;
  }, 0);
}

function getLiveChargedKwh(stationStatus, useLiveCharged) {
  const lastJob = stationStatus?.lastJob;
  if (useLiveCharged && lastJob && isActiveChargingState(lastJob.state)) {
    return Number(lastJob.chargedWtH || 0) / 1000;
  }
  return 0;
}

/**
 * Header kWh left during EC charge: kWh balance - kWh charged.
 * Uses live station metrics when available, otherwise in-progress history energy.
 */
export function getDisplayEnergyCardKwh(activeEnergyCards, stationStatus, options = {}) {
  const { ecCharging = false, useLiveCharged = false, historyData } = options;
  const balanceKwh = getEnergyCardBalanceKwh(activeEnergyCards);

  if (!ecCharging) {
    return Math.max(0, balanceKwh);
  }

  const liveChargedKwh = getLiveChargedKwh(stationStatus, useLiveCharged);
  const chargedKwh = liveChargedKwh || getHistoryEnergyCardChargedKwh(historyData);

  return Math.max(0, balanceKwh - chargedKwh);
}

/**
 * Header UAH left on UAH energy cards during EC charge.
 * Subtracts session cost when centsPerKwh is known.
 */
export function getDisplayEnergyCardUah(activeEnergyCards, stationStatus, options = {}) {
  const { ecCharging = false, useLiveCharged = false, historyData, centsPerKwh } = options;
  const balanceUah = getEnergyCardBalanceUah(activeEnergyCards);

  if (!ecCharging) {
    return Math.max(0, balanceUah);
  }

  const tariff = Number(centsPerKwh);
  if (!Number.isFinite(tariff) || tariff <= 0) {
    return Math.max(0, balanceUah);
  }

  const liveChargedKwh = getLiveChargedKwh(stationStatus, useLiveCharged);
  const chargedKwh = liveChargedKwh || getHistoryEnergyCardChargedKwh(historyData);
  const spentUah = (chargedKwh * tariff) / 100;
  return Math.max(0, balanceUah - spentUah);
}
